import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnDestroy,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subject, of } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { AddFindMeFollowMeStepComponent } from 'src/app/pages/add-find-me-follow-me-step/containers/add-find-me-follow-me-step.component';
import { ExtensionDetailView } from '../../models/extension-detail-view.model';
import { findMeFollowMeStepSubmit } from '../../state/extension-detail.actions';
import { FindMeFollowMeStepItemView } from './models/extension-find-me-follow-me-step.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-find-me-follow-me-steps',
    styleUrls: ['./extension-find-me-follow-me-steps.component.scss'],
    templateUrl: './extension-find-me-follow-me-steps.component.html',
    standalone: false
})
export class FindMeFollowMeStepsComponent implements ControlValueAccessor, OnDestroy {
   @Input() canEdit: boolean;

   @Input() extension: ExtensionDetailView | null;

   @Output() addFindMeFollowMeStepClick = new EventEmitter<void>();

   findMeFollowMeSteps: FindMeFollowMeStepItemView[] = [];

   get activeFindMeFollowMeSteps(): FindMeFollowMeStepItemView[] {
      return this.findMeFollowMeSteps.filter((v) => !v.isDeleted);
   }

   disabled = false;

   onTouched: () => void;

   private destroyed$ = new Subject<void>();

   private onChange: (value: Array<FindMeFollowMeStepItemView> | null) => void;

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      control: NgControl,
      private actions$: Actions,
      private confirmationService: ConfirmationDialogService,
      private dialogService: LcsDialogService
   ) {
      control.valueAccessor = this;
      this.actions$.pipe(ofType(findMeFollowMeStepSubmit), takeUntil(this.destroyed$)).subscribe((action) => {
         const fmFmStepToAdd: FindMeFollowMeStepItemView = {
            sequence: this.activeFindMeFollowMeSteps.length + 1,
            fmFmStepId: 0,
            extensionsOrPhoneNumbersToDial: action.findMeFollowMeStepItem.extensionsOrPhoneNumbersToDial,
            isDeleted: false,
            timeout: action.findMeFollowMeStepItem.timeout,
         };
         this.findMeFollowMeSteps.push(fmFmStepToAdd);
         this.onChange(this.findMeFollowMeSteps);
         this.changeDetectorRef.markForCheck();
      });
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   writeValue(findMeFollowMeStepItems: FindMeFollowMeStepItemView[]): void {
      if (findMeFollowMeStepItems?.length > 0) {
         this.findMeFollowMeSteps = this.sortFindMeFollowMeSteps(
            findMeFollowMeStepItems.map((item) => {
               return { ...item, isDeleted: false };
            })
         );
      } else {
         this.findMeFollowMeSteps = [];
      }
   }
   registerOnChange(onChange: (value: Array<FindMeFollowMeStepItemView> | null) => void): void {
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   setDisabledState?(isDisabled: boolean): void {
      this.disabled = isDisabled;
      this.changeDetectorRef.markForCheck();
   }

   private sortFindMeFollowMeSteps(timeBasedRoutesList: FindMeFollowMeStepItemView[]): FindMeFollowMeStepItemView[] {
      return timeBasedRoutesList.sort((t1, t2) => {
         if (!t1.sequence || !t2.sequence) {
            return 0;
         }

         if (!t1.sequence || t1.sequence > t2.sequence) {
            return 1;
         }

         if (!t2.sequence || t1.sequence < t2.sequence) {
            return -1;
         }

         return 0;
      });
   }

   private reOrderActiveSteps(): void {
      this.activeFindMeFollowMeSteps.forEach((promptItem, newSortId) => {
         promptItem.sequence = newSortId + 1;
      });
   }

   drop(event: CdkDragDrop<FindMeFollowMeStepItemView[]>): void {
      moveItemInArray(this.findMeFollowMeSteps, event.previousIndex, event.currentIndex);
      this.reOrderActiveSteps();

      this.findMeFollowMeSteps = [...this.findMeFollowMeSteps];
      this.onChange(this.findMeFollowMeSteps);
      this.changeDetectorRef.markForCheck();
   }

   onAddFindMeFollowMeStepClick() {
      this.addFindMeFollowMeStepClick.emit();
   }

   onEditFindMeFollowMeStepClick(index: number) {
      const dialogRef = this.dialogService.openFormComponent(AddFindMeFollowMeStepComponent, {
         closeOnNavigation: true,
         data: {
            extension: this.extension,
            findMeFollowMeStepFormData: JSON.parse(JSON.stringify(this.findMeFollowMeSteps[index])),
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });

      dialogRef
         .afterClosed()
         .pipe(
            filter((fmFmStepItem) => {
               return fmFmStepItem !== undefined;
            })
         )
         .subscribe((fmFmStepItem) => {
            this.findMeFollowMeSteps = [...this.findMeFollowMeSteps];
            this.findMeFollowMeSteps[index] = fmFmStepItem;
            this.onChange(this.findMeFollowMeSteps);
            this.changeDetectorRef.markForCheck();
         });
   }

   onDeleteClick(index: number) {
      this.deleteConfirmationDialog()
         .pipe(takeUntil(this.destroyed$))
         .subscribe((confirmed) => {
            if (confirmed) {
               this.findMeFollowMeSteps[index].isDeleted = true;
               this.reOrderActiveSteps();
               this.changeDetectorRef.markForCheck();
               this.onChange(this.findMeFollowMeSteps);
            }
         });
   }

   deleteConfirmationDialog(): Observable<boolean> {
      const message = 'Changes will not take effect until you save the form.';

      const dialogRef = this.confirmationService.confirm({
         data: {
            header: 'Delete this Find Me/Follow Me Step?',
            message,
         },
         size: DialogSize.Medium,
      });

      return dialogRef.afterClosed().pipe(
         takeUntil(this.destroyed$),
         switchMap((confirmed) => {
            if (confirmed) {
               return of(true);
            } else {
               return of(false);
            }
         })
      );
   }
}
